// Klipplistan på videosidan. Att klicka ett klipp byter ut inbäddningen i
// spelaren ovanför. Uppspelning är alltid manuell — aldrig autoplay.

const ACTIVE_CLASS = "clip-card--active";

interface PlayerElements {
    embed: HTMLElement;
    title: HTMLElement;
    meta: HTMLElement;
    poster: HTMLElement | null;
}

function getPlayerElements(player: HTMLElement): PlayerElements | null {
    const embed = player.querySelector<HTMLElement>("[data-player-embed]");
    const title = player.querySelector<HTMLElement>(".player__title");
    const meta = player.querySelector<HTMLElement>(".player__meta");
    const poster = player.querySelector<HTMLElement>("[data-player-poster]");

    if (!embed || !title || !meta) {
        return null;
    }

    return { embed, title, meta, poster };
}

// Miniatyrbilden täcker filmen igen varje gång ett nytt klipp väljs,
// så att inget klipp börjar spela av sig självt.
function renderPoster(poster: HTMLElement | null, thumbnail: string, title: string): void {
    if (!poster) {
        return;
    }

    if (thumbnail) {
        poster.style.backgroundImage = `url('${thumbnail}')`;
        poster.hidden = false;
    } else {
        poster.style.removeProperty("background-image");
        poster.hidden = true;
    }

    poster.setAttribute("aria-label", `Spela videon ${title}`);
}

function getClipEmbed(root: HTMLElement, clipId: string): DocumentFragment | null {
    const template = root.querySelector<HTMLTemplateElement>(
        `template[data-clip-embed='${clipId}']`
    );

    return template ? (template.content.cloneNode(true) as DocumentFragment) : null;
}

// Klick på miniatyrbilden ska både ta bort bilden och starta filmen, så att
// besökaren slipper klicka två gånger. Autoplay sätts bara efter klicket.
function startPlayback(embed: HTMLElement): void {
    const video = embed.querySelector<HTMLVideoElement>("video");

    if (video) {
        void video.play();
        return;
    }

    const iframe = embed.querySelector<HTMLIFrameElement>("iframe");

    if (!iframe || !iframe.src) {
        return;
    }

    const url = new URL(iframe.src, window.location.href);
    url.searchParams.set("autoplay", "1");
    iframe.src = url.toString();
}

function initVideoPage(root: HTMLElement): void {
    const player = root.querySelector<HTMLElement>(".player");
    const clips = Array.from(root.querySelectorAll<HTMLButtonElement>(".clip-card"));

    if (!player || clips.length === 0) {
        return;
    }

    const elements = getPlayerElements(player);

    if (!elements) {
        return;
    }

    let active = clips.find((clip) => clip.classList.contains(ACTIVE_CLASS)) ?? clips[0];

    const render = (moveFocus: boolean): void => {
        const title = active.dataset.clipTitle ?? "";
        const duration = active.dataset.clipDuration ?? "";
        const clipId = active.dataset.clipId ?? "";

        elements.title.textContent = title;
        elements.meta.textContent = duration ? `${duration} · undertextad` : "undertextad";
        player.setAttribute("aria-label", `Videospelare: ${title}`);

        const embed = getClipEmbed(root, clipId);

        if (embed) {
            elements.embed.replaceChildren(embed);
        }

        renderPoster(elements.poster, active.dataset.clipThumbnail ?? "", title);

        clips.forEach((clip) => {
            const isActive = clip === active;
            clip.classList.toggle(ACTIVE_CLASS, isActive);

            if (isActive) {
                clip.setAttribute("aria-current", "true");
            } else {
                clip.removeAttribute("aria-current");
            }
        });

        // Innehållet byts ovanför klipplistan, så fokus flyttas dit för att
        // tangentbord och skärmläsare ska hamna på den nya filmen.
        if (moveFocus) {
            player.focus();
            player.scrollIntoView({ block: "nearest" });
        }
    };

    clips.forEach((clip) => {
        clip.addEventListener("click", () => {
            if (clip === active) {
                return;
            }

            // Byte av klipp startar aldrig uppspelningen av sig självt.
            active = clip;
            render(true);
        });
    });
}

// Miniatyrbilder finns både på videosidan och på startsidan, därför kopplas
// klicket på alla spelare oavsett sida.
export function initPlayerPosters(): void {
    document.querySelectorAll<HTMLElement>("[data-player-poster]").forEach((poster) => {
        const embed = poster
            .closest(".player")
            ?.querySelector<HTMLElement>("[data-player-embed]");

        if (!embed) {
            return;
        }

        poster.addEventListener("click", () => {
            poster.hidden = true;
            startPlayback(embed);
        });
    });
}

export function initVideoPages(): void {
    initPlayerPosters();

    document
        .querySelectorAll<HTMLElement>("[data-breaks-video]")
        .forEach((root) => initVideoPage(root));
}

export default initVideoPages;
